import {
  EPropertyType,
  getQuickPropShapeIssue,
  IComponent,
  IProperty,
  IQuickEventRef,
  IQuickProp,
  IQuickPropertyRef,
  IQuickPropIssue,
  isQuickStyleHidden,
  QUICK_PROPS_MAX,
  quickPropIdentity,
  QuickStyleKey,
  validateQuickProps
} from "@kuika/kuika-cl-model";
import * as React from "react";
import {
  DragDropContext,
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
  DropResult,
  Droppable
} from "react-beautiful-dnd";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaEmptyState from "../../../ui-kit/kuika-empty-state/kuika-empty-state";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSwitch from "../../../ui-kit/kuika-switch/kuika-switch";
import KuikaSmallBoldText from "../../../ui-kit/typography/SmallBoldText/SmallBoldText";
import KuikaSmallMediumText from "../../../ui-kit/typography/SmallMediumText/SmallMediumText";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import "./component-def-quick-props-step.scss";

/**
 * Display label per recognized `QuickStyleKey`, in the order the "Available" column lists them. Typed as a
 * full `Record` so a style key added to kuika-cl-model fails to compile here until it has a label; which
 * keys exist and what hides each one comes from the validator (`isQuickStyleHidden`), never a copy.
 */
const QUICK_STYLE_LABELS: Record<QuickStyleKey, string> = {
  "text.fontFamily": "Font Family",
  "text.fontSize": "Font Size",
  "text.format": "Text Format (weight)",
  "text.color": "Text Color",
  "fill.color": "Fill Color",
  "position.textAlign": "Text Align"
};

const QUICK_STYLE_KEYS_IN_ORDER: QuickStyleKey[] = Object.keys(QUICK_STYLE_LABELS) as QuickStyleKey[];

const KIND_BADGE_LABEL: Record<IQuickProp["kind"], string> = {
  property: "Property",
  propertyGroup: "Property group",
  event: "Event",
  style: "Style",
  command: "Command",
  children: "Children"
};

/**
 * Whether a stored entry is one this build can read. Component-set data can be malformed (`null`, a
 * non-string label, from hand-edited or corrupted JSON) or authored by a newer designer build (an unknown
 * `kind` / style key); every helper below goes through this — the same classification `validateQuickProps`
 * reports through `issues()` — before reading any field, so a bad entry never reaches a React child.
 */
function isRecognizedQuickProp(value: unknown): value is IQuickProp {
  return getQuickPropShapeIssue(value) === undefined;
}

/**
 * A key per row that survives reordering (unlike its index): the entry's identity plus how many earlier
 * rows share it, so duplicates and unrecognized entries still get distinct keys.
 */
function quickPropRowKeys(quickProps: readonly unknown[]): string[] {
  const seen: Map<string, number> = new Map<string, number>();
  return quickProps.map((entry: unknown): string => {
    const identity: string = isRecognizedQuickProp(entry) ? quickPropIdentity(entry) : "unrecognized";
    const occurrence: number = seen.get(identity) ?? 0;
    seen.set(identity, occurrence + 1);
    return `${identity}#${occurrence}`;
  });
}

function quickPropLabel(prop: IQuickProp): string {
  switch (prop.kind) {
    case "property":
      return prop.name;
    case "event":
      return prop.name;
    case "style":
      return QUICK_STYLE_LABELS[prop.key];
    case "command":
      return prop.label ?? `Add ${prop.childName}`;
    case "propertyGroup":
      return prop.label;
    case "children":
      return prop.label;
    default: {
      const exhaustive: never = prop;
      return String(exhaustive);
    }
  }
}

/** One row in an "Available" candidate group. */
interface IQuickPropCandidate {
  candidateKey: string;
  label: string;
  onAdd: () => void;
}

/** Pure reorder step, mirroring `reorderProperties` in `component-def-second-step.tsx`. Exported so the
 *  drag-reorder outcome is unit-testable without simulating `react-beautiful-dnd`'s pointer events. */
export function reorderQuickProps(
  quickProps: IQuickProp[],
  sourceIndex: number,
  destinationIndex: number
): IQuickProp[] {
  const next: IQuickProp[] = [...quickProps];
  const [moved] = next.splice(sourceIndex, 1);
  next.splice(destinationIndex, 0, moved);
  return next;
}

export interface IComponentQuickPropsStepProps {
  component: IComponent;
  /** The live, possibly-unsaved property list from the Properties step (not `component.properties`) —
   *  a property renamed there but not yet saved must be reflected here too. */
  properties: IProperty[];
  inVisibleStyles: string[];
  childrenNames: string[];
  quickProps: IQuickProp[];
  onChange: (quickProps: IQuickProp[]) => void;
}

export class ComponentFormQuickPropsStep extends React.PureComponent<IComponentQuickPropsStepProps> {
  /** `this.props.quickProps`, defensively normalized to an array. Component-set data can be malformed or
   *  saved by a different designer build (e.g. `null`/non-array), so every list/render helper below reads
   *  through this instead of trusting the prop's declared type. */
  private get quickProps(): IQuickProp[] {
    return Array.isArray(this.props.quickProps) ? this.props.quickProps : [];
  }

  private issues(): IQuickPropIssue[] {
    return validateQuickProps({
      name: this.props.component?.name ?? "",
      properties: this.props.properties ?? [],
      inVisibleStyles: this.props.inVisibleStyles ?? [],
      childrenNames: this.props.childrenNames ?? [],
      quickProps: this.quickProps
    });
  }

  private addQuickProp = (prop: IQuickProp): void => {
    const quickProps: IQuickProp[] = this.quickProps;
    if (quickProps.length >= QUICK_PROPS_MAX) return;
    this.props.onChange([...quickProps, prop]);
  };

  private removeAt = (index: number): void => {
    const next: IQuickProp[] = [...this.quickProps];
    next.splice(index, 1);
    this.props.onChange(next);
  };

  private toggleWarnWhenEmpty = (index: number, warnWhenEmpty: boolean): void => {
    const next: IQuickProp[] = [...this.quickProps];
    const item: IQuickProp = next[index];
    if (isRecognizedQuickProp(item) && (item.kind === "property" || item.kind === "event")) {
      next[index] = { ...item, warnWhenEmpty };
      this.props.onChange(next);
    }
  };

  private onDragEnd = (result: DropResult): void => {
    if (!result.destination) return;
    this.props.onChange(reorderQuickProps(this.quickProps, result.source.index, result.destination.index));
  };

  private existingKeys(kind: IQuickProp["kind"]): Set<string> {
    return new Set(
      this.quickProps
        .filter((p: IQuickProp): boolean => isRecognizedQuickProp(p) && p.kind === kind)
        .map((p: IQuickProp): string => quickPropIdentity(p))
    );
  }

  private availablePropertyCandidates(): IQuickPropCandidate[] {
    const existing: Set<string> = this.existingKeys("property");
    return (this.props.properties ?? [])
      .filter((p: IProperty): boolean => {
        if (!p.name) return false;
        if (existing.has(`property:${p.name}`)) return false;
        if (p.isTechnical) return false;
        if (p.isDeprecated) return false;
        return !(p.type ?? []).includes(EPropertyType.event);
      })
      .map((p: IProperty): IQuickPropCandidate => ({
        // Bare name, per spec — a property and a child cannot collide (verified against the ~187
        // shipped Backend/CoreApi/Metadatas/Components/*.json definitions: 0 hits), and events come
        // from a disjoint subset of this same properties array so cannot collide with each other.
        candidateKey: p.name,
        label: p.name,
        onAdd: (): void => this.addQuickProp({ kind: "property", name: p.name })
      }));
  }

  private availableEventCandidates(): IQuickPropCandidate[] {
    const existing: Set<string> = this.existingKeys("event");
    return (this.props.properties ?? [])
      .filter((p: IProperty): boolean => {
        if (!p.name) return false;
        if (existing.has(`event:${p.name}`)) return false;
        return (p.type ?? []).includes(EPropertyType.event);
      })
      .map((p: IProperty): IQuickPropCandidate => ({
        candidateKey: p.name,
        label: p.name,
        onAdd: (): void => this.addQuickProp({ kind: "event", name: p.name })
      }));
  }

  private availableStyleCandidates(): IQuickPropCandidate[] {
    const existing: Set<string> = this.existingKeys("style");
    const inVisibleStyles: string[] = this.props.inVisibleStyles ?? [];
    return QUICK_STYLE_KEYS_IN_ORDER.filter(
      (key: QuickStyleKey): boolean => !existing.has(`style:${key}`) && !isQuickStyleHidden(key, inVisibleStyles)
    ).map((key: QuickStyleKey): IQuickPropCandidate => ({
      candidateKey: key,
      label: QUICK_STYLE_LABELS[key],
      onAdd: (): void => this.addQuickProp({ kind: "style", key })
    }));
  }

  private availableCommandCandidates(): IQuickPropCandidate[] {
    const existing: Set<string> = this.existingKeys("command");
    return (this.props.childrenNames ?? [])
      .filter((name: string): boolean => Boolean(name) && !existing.has(`command:${name}`))
      .map((name: string): IQuickPropCandidate => ({
        candidateKey: name,
        label: `Add ${name}`,
        onAdd: (): void => this.addQuickProp({ kind: "command", command: "addChild", childName: name })
      }));
  }

  private renderCandidateGroup(title: string, items: IQuickPropCandidate[], atMax: boolean): React.ReactNode {
    if (items.length === 0) return null;
    return (
      <div className="kuika-quick-props-step__group" key={title}>
        <KuikaSmallText className="kuika-quick-props-step__group-title">{title}</KuikaSmallText>
        <div className="kuika-quick-props-step__candidates">
          {items.map((item: IQuickPropCandidate): React.ReactNode => (
            <div
              className="kuika-quick-props-step__candidate"
              key={item.candidateKey}
              data-testid={`quickPropsAvailable-${item.candidateKey}`}
            >
              <KuikaSmallText className="kuika-quick-props-step__candidate-label">{item.label}</KuikaSmallText>
              <KuikaButton
                data-testid={`quickPropsAdd-${item.candidateKey}`}
                type="tertiary"
                size="sm"
                disabled={atMax}
                aria-label={`Add ${item.label} to the quick props toolbar`}
                onClick={item.onAdd}
              >
                <KuikaSvg name="plus" width={14} height={14} color="currentColor" />
                Add
              </KuikaButton>
            </div>
          ))}
        </div>
      </div>
    );
  }

  private renderRow(
    prop: IQuickProp,
    index: number,
    rowIssues: IQuickPropIssue[],
    dragProvided: DraggableProvided,
    dragSnapshot: DraggableStateSnapshot
  ): JSX.Element {
    // An entry this build can't read (malformed, or from a newer designer) has no trustworthy field to
    // take a label/badge/warn-toggle from — but it still needs a row: a working Remove button and its
    // `issues()` message(s), rendered below via `rowIssues`.
    const isRenderable: boolean = isRecognizedQuickProp(prop);
    const label: string = isRenderable ? quickPropLabel(prop) : "Unrecognized entry";
    const kindLabel: string = isRenderable ? KIND_BADGE_LABEL[prop.kind] : "Unrecognized";
    const hasWarnToggle: boolean = isRenderable && (prop.kind === "property" || prop.kind === "event");
    return (
      <div
        className={`kuika-quick-props-step__row${
          dragSnapshot.isDragging ? " kuika-quick-props-step__row--dragging" : ""
        }${rowIssues.length > 0 ? " kuika-quick-props-step__row--issue" : ""}`}
        ref={dragProvided.innerRef}
        {...dragProvided.draggableProps}
        data-testid={`quickPropsItem-${index}`}
      >
        <div className="kuika-quick-props-step__row-main">
          <span
            className="kuika-quick-props-step__handle"
            data-testid={`quickPropsDragHandle-${index}`}
            aria-label={`Reorder ${label}`}
            {...dragProvided.dragHandleProps}
          >
            <KuikaSvg name="grip-vertical" width={16} height={16} color="var(--dark-5)" />
          </span>

          <span className="kuika-quick-props-step__kind">
            <KuikaSmallText>{kindLabel}</KuikaSmallText>
          </span>

          <span className="kuika-quick-props-step__label">
            <KuikaSmallMediumText>{label}</KuikaSmallMediumText>
          </span>

          {hasWarnToggle && (
            <KuikaSwitch
              className="kuika-quick-props-step__warn"
              dataTestId={`quickPropsWarnWhenEmpty-${index}`}
              label="Warn when empty"
              labelPosition="left"
              checked={(prop as IQuickPropertyRef | IQuickEventRef).warnWhenEmpty === true}
              onChange={(checked: boolean): void => this.toggleWarnWhenEmpty(index, checked)}
            />
          )}

          <KuikaButton
            data-testid={`quickPropsRemove-${index}`}
            type="tertiary"
            color="danger"
            size="sm"
            isIcon
            aria-label={`Remove ${label}`}
            onClick={(): void => this.removeAt(index)}
          >
            <KuikaSvg name="trash" width={16} height={16} color="currentColor" />
          </KuikaButton>
        </div>

        {rowIssues.length > 0 && (
          // The testid lives on the container, not each `.__issue` span: a row can carry more than
          // one issue (e.g. a propertyGroup with two dangling members), which would otherwise render
          // two elements sharing the same `quickPropsIssue-<index>` id and break `getByTestId`.
          <div className="kuika-quick-props-step__row-issues" data-testid={`quickPropsIssue-${index}`}>
            {rowIssues.map((issue: IQuickPropIssue, issueIdx: number): React.ReactNode => (
              <span className="kuika-quick-props-step__issue" key={issueIdx}>
                <KuikaSvg name="alert-triangle" width={14} height={14} color="var(--red-5)" />
                <KuikaSmallText className="kuika-quick-props-step__issue-text">{issue.message}</KuikaSmallText>
              </span>
            ))}
          </div>
        )}
      </div>
    );
  }

  render(): React.ReactNode {
    const quickProps: IQuickProp[] = this.quickProps;
    const rowKeys: string[] = quickPropRowKeys(quickProps);
    const atMax: boolean = quickProps.length >= QUICK_PROPS_MAX;
    const issues: IQuickPropIssue[] = this.issues();
    const issuesByIndex: Map<number, IQuickPropIssue[]> = new Map();
    issues.forEach((issue: IQuickPropIssue): void => {
      const list: IQuickPropIssue[] = issuesByIndex.get(issue.index) ?? [];
      list.push(issue);
      issuesByIndex.set(issue.index, list);
    });

    const propertyCandidates: IQuickPropCandidate[] = this.availablePropertyCandidates();
    const eventCandidates: IQuickPropCandidate[] = this.availableEventCandidates();
    const styleCandidates: IQuickPropCandidate[] = this.availableStyleCandidates();
    const commandCandidates: IQuickPropCandidate[] = this.availableCommandCandidates();
    const hasAnyCandidate: boolean =
      propertyCandidates.length > 0 ||
      eventCandidates.length > 0 ||
      styleCandidates.length > 0 ||
      commandCandidates.length > 0;

    return (
      <div className="kuika-quick-props-step" data-testid="quickPropsStep">
        <KuikaSmallText className="kuika-quick-props-step__caption">
          Pick up to {QUICK_PROPS_MAX} properties, events, styles or commands to surface in the canvas selection toolbar
          when this component is selected.
        </KuikaSmallText>

        <div className="kuika-quick-props-step__columns">
          <section className="kuika-quick-props-step__column">
            <KuikaSmallBoldText className="kuika-quick-props-step__column-title">Available</KuikaSmallBoldText>

            {this.renderCandidateGroup("Properties", propertyCandidates, atMax)}
            {this.renderCandidateGroup("Events", eventCandidates, atMax)}
            {this.renderCandidateGroup("Text & fill styles", styleCandidates, atMax)}
            {this.renderCandidateGroup("Children commands", commandCandidates, atMax)}

            {!hasAnyCandidate && (
              <KuikaEmptyState
                variant="embedded"
                svgName="components"
                customName="Nothing left to add"
                description="Every eligible property, event, style and child command is already in the toolbar."
              />
            )}
          </section>

          <section className="kuika-quick-props-step__column">
            <div className="kuika-quick-props-step__column-head">
              <KuikaSmallBoldText className="kuika-quick-props-step__column-title">In toolbar</KuikaSmallBoldText>
              <KuikaSmallText data-testid="quickPropsCounter" className="kuika-quick-props-step__counter">
                {`${quickProps.length} / ${QUICK_PROPS_MAX}`}
              </KuikaSmallText>
            </div>

            {quickProps.length === 0 ? (
              <KuikaEmptyState
                variant="embedded"
                svgName="components"
                customName="Nothing in the toolbar yet"
                description="Add a property, event, style or command from the left to surface it on the canvas."
              />
            ) : (
              <DragDropContext onDragEnd={this.onDragEnd}>
                <Droppable droppableId="quick-props-droppable">
                  {(provided) => (
                    <div className="kuika-quick-props-step__rows" {...provided.droppableProps} ref={provided.innerRef}>
                      {quickProps.map((prop: IQuickProp, index: number): React.ReactNode => (
                        <Draggable key={rowKeys[index]} draggableId={`quick-prop-${rowKeys[index]}`} index={index}>
                          {(dragProvided: DraggableProvided, dragSnapshot: DraggableStateSnapshot) =>
                            this.renderRow(prop, index, issuesByIndex.get(index) ?? [], dragProvided, dragSnapshot)
                          }
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </section>
        </div>
      </div>
    );
  }
}
